import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import ReactMarkdown from 'react-markdown'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ChevronDown } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useI18n } from '@/lib/i18n'
import {
  SpeechPlaybackControl,
  SpeechPlaybackStatusAccent,
  stopActiveSpeechPlayback,
  useSpeechPlayback,
} from './SpeechPlaybackControl'
import type { TextLogSpeechReference } from './speech-references'

const SPEECH_CONTROL_MARKER = Symbol('speech-control-marker')
type SpeechMarkerKind = 'accent' | 'control'
const EMPTY_SPEECH_REFERENCES: readonly TextLogSpeechReference[] = []
const LOG_BOTTOM_THRESHOLD_PX = 2
const useClientLayoutEffect
  = typeof window === 'undefined' ? useEffect : useLayoutEffect
const SPEECH_SOURCE_CONTAINERS = new Set([
  'root',
  'paragraph',
  'heading',
  'listItem',
  'blockquote',
  'tableCell',
])

const LOG_SANITIZE_SCHEMA = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      'dataSpeechTtsId',
      'dataSpeechMarkerKind',
      'dataSpeechControlToken',
    ],
  },
}

interface HastNode {
  type: string
  tagName?: string
  value?: string
  properties?: Record<string, unknown>
  children?: HastNode[]
  position?: {
    start: { offset?: number }
    end: { offset?: number }
  }
  [SPEECH_CONTROL_MARKER]?: SpeechMarkerKind
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
}

function speechMarkerHtml(
  reference: TextLogSpeechReference,
  nonce: string,
  kind: SpeechMarkerKind,
): HastNode {
  return {
    type: 'html',
    value: `<span data-speech-control-token="${escapeHtmlAttribute(nonce)}" data-speech-marker-kind="${kind}" data-speech-tts-id="${escapeHtmlAttribute(reference.id)}"></span>`,
  }
}

function splitTextAtBoundary(
  parent: HastNode,
  childIndex: number,
  boundary: number,
  marker: HastNode,
): boolean {
  const child = parent.children?.[childIndex]
  const start = child?.position?.start.offset
  const end = child?.position?.end.offset
  if (
    !child
    || child.type !== 'text'
    || typeof child.value !== 'string'
    || typeof start !== 'number'
    || typeof end !== 'number'
    || child.value.length !== end - start
    || boundary <= start
    || boundary >= end
  ) {
    return false
  }
  const splitIndex = boundary - start
  const before: HastNode = {
    ...child,
    value: child.value.slice(0, splitIndex),
    position: { start: { offset: start }, end: { offset: boundary } },
  }
  const after: HastNode = {
    ...child,
    value: child.value.slice(splitIndex),
    position: { start: { offset: boundary }, end: { offset: end } },
  }
  parent.children!.splice(childIndex, 1, before, marker, after)
  return true
}

function insertSpeechMarker(
  node: HastNode,
  boundary: number,
  marker: HastNode,
): boolean {
  if (!node.children?.length)
    return false

  const containing = node.children
    .map((child, index) => ({ child, index }))
    .filter(({ child }) => {
      const start = child.position?.start.offset
      const end = child.position?.end.offset
      return typeof start === 'number'
        && typeof end === 'number'
        && start < boundary
        && boundary < end
    })
  if (containing.length > 1)
    return false
  if (containing.length === 1) {
    const { child, index } = containing[0]!
    if (child.type === 'text')
      return splitTextAtBoundary(node, index, boundary, marker)
    return insertSpeechMarker(child, boundary, marker)
  }

  const endingContainers = node.children.filter(child =>
    child.position?.end.offset === boundary
    && SPEECH_SOURCE_CONTAINERS.has(child.type))
  if (endingContainers.length > 1)
    return false
  if (
    endingContainers.length === 1
    && insertSpeechMarker(endingContainers[0]!, boundary, marker)
  ) {
    return true
  }

  const insertionIndexes = new Set<number>()
  node.children.forEach((child, index) => {
    if (child.position?.end.offset === boundary)
      insertionIndexes.add(index + 1)
    if (child.position?.start.offset === boundary)
      insertionIndexes.add(index)
  })
  if (insertionIndexes.size !== 1)
    return false
  node.children.splice([...insertionIndexes][0]!, 0, marker)
  return true
}

function createSpeechControlPlugins(
  references: readonly TextLogSpeechReference[],
) {
  const nonce = `speech-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
  const expectedIds = new Set(references.map(reference => reference.id))
  const remark = function remark() {
    return (tree: HastNode) => {
      const speechMarkers = references.flatMap(reference => [
        {
          boundary: reference.headerPlacement.end,
          kind: 'control' as const,
          reference,
        },
        ...(reference.headerPlacement.accentEnd === undefined
          ? []
          : [{
              boundary: reference.headerPlacement.accentEnd,
              kind: 'accent' as const,
              reference,
            }]),
      ])
      const markers = speechMarkers
        .sort((left, right) => right.boundary - left.boundary)
      for (const marker of markers) {
        insertSpeechMarker(
          tree,
          marker.boundary,
          speechMarkerHtml(marker.reference, nonce, marker.kind),
        )
      }
    }
  }
  const rehype = function rehype() {
    return (tree: HastNode) => {
      const consumed = new Set<string>()
      const visit = (node: HastNode) => {
        const id = node.properties?.dataSpeechTtsId
        const token = node.properties?.dataSpeechControlToken
        const kind = node.properties?.dataSpeechMarkerKind
        const markerIdentity = `${kind}:${id}`
        if (
          node.type === 'element'
          && node.tagName === 'span'
          && token === nonce
          && typeof id === 'string'
          && (kind === 'accent' || kind === 'control')
          && expectedIds.has(id)
          && !consumed.has(markerIdentity)
        ) {
          consumed.add(markerIdentity)
          delete node.properties!.dataSpeechControlToken
          delete node.position
          node[SPEECH_CONTROL_MARKER] = kind
        }
        node.children?.forEach(child => visit(child))
      }
      visit(tree)
    }
  }
  return { remark, rehype }
}

export function LogContent({
  content,
  speechReferences = EMPTY_SPEECH_REFERENCES,
  scopeKey = 'game-log',
  active = true,
}: {
  content: string
  speechReferences?: readonly TextLogSpeechReference[]
  scopeKey?: string
  active?: boolean
}) {
  const { t } = useI18n()
  const [isAtBottom, setIsAtBottom] = useState(true)
  const viewportRef = useRef<HTMLDivElement>(null)
  const lastScrollTopRef = useRef(0)
  const hasMeasuredViewportRef = useRef(false)
  const previousLogRef = useRef({
    content,
    scopeKey,
  })
  const eligibleReferences = useMemo(
    () => speechReferences.filter(reference => reference.ttsEligible),
    [speechReferences],
  )
  const referencesById = useMemo(
    () => new Map(eligibleReferences.map(reference => [reference.id, reference])),
    [eligibleReferences],
  )
  const speechControlPlugins = useMemo(
    () => createSpeechControlPlugins(eligibleReferences),
    [eligibleReferences],
  )
  const playback = useSpeechPlayback(eligibleReferences, scopeKey)
  const stopPlayback = playback.stop
  useEffect(() => {
    if (active)
      stopActiveSpeechPlayback()
    else
      stopPlayback()
  }, [active, stopPlayback])

  const updateBottomState = useCallback((viewport: HTMLDivElement) => {
    lastScrollTopRef.current = viewport.scrollTop
    const atBottom
      = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
        < LOG_BOTTOM_THRESHOLD_PX
    setIsAtBottom(atBottom)
  }, [])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    updateBottomState(e.currentTarget)
  }, [updateBottomState])

  useClientLayoutEffect(() => {
    const viewport = viewportRef.current
    const previousLog = previousLogRef.current
    previousLogRef.current = {
      content,
      scopeKey,
    }
    if (!viewport)
      return

    if (!hasMeasuredViewportRef.current) {
      hasMeasuredViewportRef.current = true
      updateBottomState(viewport)
      return
    }

    if (previousLog.scopeKey !== scopeKey) {
      viewport.scrollTop = 0
      updateBottomState(viewport)
      return
    }

    if (previousLog.content !== content)
      viewport.scrollTop = lastScrollTopRef.current

    updateBottomState(viewport)
  }, [content, scopeKey, updateBottomState])

  const scrollToBottom = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport)
      return
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' })
  }, [])

  return (
    <div className="relative h-full">
      <ScrollArea className="h-full max-h-full" viewportRef={viewportRef} onScroll={handleScroll}>
        <div className="prose prose-sm max-w-none p-4 dark:prose-invert">
          <ReactMarkdown
            remarkPlugins={[
              remarkGfm,
              speechControlPlugins.remark,
              remarkBreaks,
            ]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, LOG_SANITIZE_SCHEMA], speechControlPlugins.rehype]}
            components={{
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              span: ({ node, children, ...props }) => {
                const speechNode = node as HastNode | undefined
                const id = speechNode?.properties?.dataSpeechTtsId
                const markerKind = speechNode?.[SPEECH_CONTROL_MARKER]
                const reference = markerKind
                  && speechNode.position === undefined
                  && typeof id === 'string'
                  ? referencesById.get(id)
                  : undefined
                if (reference && markerKind === 'control') {
                  return (
                    <SpeechPlaybackControl
                      reference={reference}
                      controller={playback}
                    />
                  )
                }
                if (reference && markerKind === 'accent') {
                  return (
                    <SpeechPlaybackStatusAccent
                      reference={reference}
                      controller={playback}
                    />
                  )
                }
                return <span {...props}>{children}</span>
              },
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </ScrollArea>
      {!isAtBottom && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-primary/60 px-3 py-1 text-xs text-primary-foreground shadow-lg backdrop-blur-sm transition-opacity hover:bg-primary/80"
        >
          <ChevronDown className="size-3" />
          {t('play.moreLogsBelow')}
        </button>
      )}
    </div>
  )
}
